import { useState } from 'react';
import { checkPrintingThreshold } from '@app/services/franchise';
import { toast } from 'react-toastify';

interface ThresholdCheckPayload {
    franchise_id: number;
    student_id: number;
    subject_id: number;
    type: 'adhoc' | 'setwork';
    worksheets_to_print: number;
    assignment_dates?: string[]; // Array of dates in YYYY-MM-DD format, required for setwork
    work_subject_id?: number; // Required for setwork type, 0 for mix and match, subject_id for single subject
}

interface ThresholdCheckResult {
    success: boolean;
    threshold_exceeded: boolean;
    subject_name?: string;
    month_type?: 'single' | 'multiple';
    month?: string;
    month_details?: Array<{
        month: string;
        threshold_worksheet_count: number;
        threshold_source: string;
        current_count: number;
        exceeded_by: number;
        threshold_exceeded: boolean;
        total_after_print: number;
        worksheets_to_print: number;
        student_id: number;
        subject_id: number;
    }>;
    details?: {
        threshold_worksheet_count: number;
        threshold_source: string;
        current_count: number;
        worksheets_to_print: number;
        total_after_print: number;
        exceeded_by: number;
        student_id: number;
        subject_id: number;
    };
}

export const usePrintThresholdCheck = () => {
    const [loading, setLoading] = useState(false);

    const checkThreshold = async (payload: ThresholdCheckPayload): Promise<ThresholdCheckResult | null> => {
        try {
            setLoading(true);
            const resp = await checkPrintingThreshold(payload);
            setLoading(false);
            
            //console.log('Threshold check response:', resp);
            
            if (resp.success === true) {
                // Extract collections data
                const data = resp.collections || resp;
                
                //console.log('Extracted data:', data);
                //console.log('Month type:', data.month_type);
                //console.log('Month details:', data.month_details);
                
                // For setwork with single month, map collections to details
                if (data.month_type === 'single' && data.threshold_exceeded) {
                    return {
                        success: true,
                        threshold_exceeded: data.threshold_exceeded,
                        month_type: data.month_type,
                        month: data.month,
                        subject_name: data.subject_name,
                        details: {
                            threshold_worksheet_count: data.threshold_worksheet_count,
                            threshold_source: data.threshold_source,
                            current_count: data.current_count,
                            worksheets_to_print: data.worksheets_to_print,
                            total_after_print: data.total_after_print,
                            exceeded_by: data.exceeded_by,
                            student_id: data.student_id,
                            subject_id: data.subject_id
                        }
                    };
                }
                
                // For setwork with multiple months, return with month_details
                if (data.month_type === 'multiple') {
                    return {
                        success: true,
                        threshold_exceeded: data.threshold_exceeded || false,
                        month_type: data.month_type,
                        subject_name: data.subject_name,
                        month_details: data.month_details,
                        details: data.details
                    };
                }
                
                // For adhoc or non-exceeded cases, return as-is
                return {
                    success: true,
                    threshold_exceeded: data.threshold_exceeded || false,
                    subject_name: data.subject_name,
                    details: data.details || data
                };
            } else {
                toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to check printing threshold');
                return null;
            }
        } catch (error: any) {
            setLoading(false);
            console.error('Threshold check error:', error);
            toast.error(error?.message || 'Failed to check printing threshold');
            return null;
        }
    };

    const checkMultipleSubjects = async (
        franchise_id: number,
        student_id: number,
        type: 'adhoc' | 'setwork',
        subjectGroups: Array<{ subject_id: number; worksheet_count: number; assignment_dates?: string[] }>,
        work_subject_id?: number
    ) => {
        try {
            setLoading(true);
            
            const thresholdChecks = await Promise.all(
                subjectGroups.map(async (group) => {
                    const payload: ThresholdCheckPayload = {
                        franchise_id,
                        student_id,
                        subject_id: group.subject_id,
                        type,
                        worksheets_to_print: group.worksheet_count
                    };
                    
                    if (group.assignment_dates) {
                        payload.assignment_dates = group.assignment_dates;
                    }
                    
                    if (type === 'setwork' && work_subject_id !== undefined) {
                        payload.work_subject_id = work_subject_id;
                    }
                    
                    const resp = await checkPrintingThreshold(payload);
                    
                    //console.log(`Threshold check response:`, resp);

                    // Treat a failed/invalid response as a hard failure so the caller
                    // can stop and surface an error instead of silently proceeding.
                    if (!resp || resp.success !== true) {
                        return { __failed: true } as any;
                    }
                    
                    // Extract collections data
                    const data = resp.collections || resp;
                    
                    // Handle single month response
                    let details = data.details;
                    let month_details = data.month_details;
                    
                    if (data.month_type === 'single') {
                        details = {
                            threshold_worksheet_count: data.threshold_worksheet_count,
                            threshold_source: data.threshold_source,
                            current_count: data.current_count,
                            worksheets_to_print: data.worksheets_to_print,
                            total_after_print: data.total_after_print,
                            exceeded_by: data.exceeded_by,
                            student_id: data.student_id,
                            subject_id: data.subject_id
                        };
                    }
                    
                    return {
                        threshold_exceeded: data.threshold_exceeded || false,
                        subject_name: data.subject_name || 'Unknown Subject', // Use subject_name from API response
                        worksheets_to_print: group.worksheet_count,
                        month_type: data.month_type,
                        details,
                        month_details
                    };
                })
            );
            
            setLoading(false);

            // If any subject check failed, signal failure to the caller (null)
            if (thresholdChecks.some((check: any) => check?.__failed)) {
                toast.error('Failed to check printing threshold. Please try again.');
                return null;
            }
            
            const exceededSubjects = thresholdChecks.filter(check => check.threshold_exceeded);
            //console.log('Exceeded subjects:', exceededSubjects);
            return exceededSubjects;
        } catch (error: any) {
            setLoading(false);
            console.error('Multiple subjects check error:', error);
            toast.error(error?.message || 'Failed to check printing threshold. Please try again.');
            return null;
        }
    };

    return {
        loading,
        checkThreshold,
        checkMultipleSubjects
    };
};
